// src/components/production/PrazoAnalysis.js
import React, { useState } from "react";
import {
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Layers,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { useProductionContext } from "../../contexts/ProductionContext";
import { getModelFromSku } from "../../utils/skuUtils";
import { analisarPrazo, formatDiasUteis, STATUS_PRIORITY } from "../../utils/prazoEngine";
import PrazoDetalhesModal from "./PrazoDetalhesModal";

export default function PrazoAnalysis({ order, compact = false, className = "" }) {
  const { prazosMap = {}, temposEtapaMap = {} } = useProductionContext() || {};
  const [modalOpen, setModalOpen] = useState(false);

  if (!order) return null;

  const rawItems = Array.isArray(order.items) && order.items.length > 0 ? order.items : [order];

  const allAnalises = rawItems.map((it, idx) => {
    const itemSku =
      it.sku ||
      it.order?.sku ||
      (idx === 0 ? order.sku || order.order?.sku : "") ||
      "";
    const itemModelo = getModelFromSku(itemSku);
    const itemPrazoRecord = itemModelo ? prazosMap[itemModelo] : null;
    const itemPrazoPrometido = itemPrazoRecord?.prazo_prometido;
    const itemFatorRisco = itemPrazoRecord?.fator_risco || 0;

    const dataCompra =
      it.customCreatedAt ||
      it.createdAt ||
      it.created_at ||
      it.date ||
      it.created ||
      order.customCreatedAt ||
      order.createdAt ||
      order.order?.created_at ||
      order.date ||
      order.created;

    const isPrazoTemporalStatus = (s) =>
      ["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(
        String(s || "").trim().toUpperCase()
      );

    const statusAtual =
      (!isPrazoTemporalStatus(it.etapaProducao) && it.etapaProducao) ||
      (!isPrazoTemporalStatus(it.statusAtual) && it.statusAtual) ||
      (!isPrazoTemporalStatus(it.production_step) && it.production_step) ||
      (!isPrazoTemporalStatus(it.status) && it.status) ||
      (!isPrazoTemporalStatus(it.step) && it.step) ||
      it.logisticsStatus ||
      (!isPrazoTemporalStatus(order.etapaProducao) && order.etapaProducao) ||
      (!isPrazoTemporalStatus(order.production_step) && order.production_step) ||
      (!isPrazoTemporalStatus(order.status) && order.status) ||
      "SOLICITACAO";

    const isIntercepted = Boolean(
      it.isInterceptedPE ||
      it.isIntercepted ||
      order.isInterceptedPE ||
      order.isIntercepted
    );

    const analise = analisarPrazo({
      dataCompra,
      statusAtual,
      prazoPrometido: itemPrazoPrometido,
      temposEtapa: temposEtapaMap,
      modelo: itemModelo,
      isIntercepted,
      fatorRisco: itemFatorRisco,
    });

    return {
      item: it,
      index: idx,
      sku: itemSku,
      modelo: itemModelo,
      analise,
    };
  });

  // Ordena pela maior criticidade
  const sorted = [...allAnalises].sort(
    (a, b) =>
      (STATUS_PRIORITY[a.analise.status] || 99) -
      (STATUS_PRIORITY[b.analise.status] || 99)
  );

  const mostCritical = sorted[0] || allAnalises[0];
  const analise = mostCritical.analise;
  const modelo = mostCritical.modelo;
  const sku = mostCritical.sku;
  const orderWithItems = { ...order, items: rawItems };

  // Configuração visual por status
  const STATUS_CONFIG = {
    PRONTO: {
      label: "PEDIDO PRONTO",
      bg: "bg-emerald-50 text-emerald-800 border-emerald-300",
      badgeBg: "bg-emerald-600 text-white",
      Icon: CheckCircle2,
    },
    ENVIADO: {
      label: "ENVIADO",
      bg: "bg-blue-50 text-blue-800 border-blue-300",
      badgeBg: "bg-blue-600 text-white",
      Icon: CheckCircle2,
    },
    EM_REVISAO: {
      label: "EM REVISÃO",
      bg: "bg-amber-50 text-amber-800 border-amber-300",
      badgeBg: "bg-amber-500 text-white",
      Icon: AlertTriangle,
    },
    NO_PRAZO: {
      label: "NO PRAZO",
      bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
      badgeBg: "bg-emerald-600 text-white",
      Icon: CheckCircle2,
    },
    APERTADO: {
      label: "APERTADO",
      bg: "bg-amber-50 text-amber-800 border-amber-200",
      badgeBg: "bg-amber-500 text-white",
      Icon: AlertTriangle,
    },
    VAI_ATRASAR: {
      label: "VAI ATRASAR",
      bg: "bg-orange-50 text-orange-800 border-orange-300",
      badgeBg: "bg-orange-500 text-white",
      Icon: AlertTriangle,
    },
    ATRASADO: {
      label: "ATRASADO",
      bg: "bg-red-50 text-red-700 border-red-300",
      badgeBg: "bg-red-600 text-white animate-pulse",
      Icon: AlertTriangle,
    },
    SEM_PRAZO: {
      label: "SEM PRAZO",
      bg: "bg-slate-50 text-slate-600 border-slate-200",
      badgeBg: "bg-slate-400 text-white",
      Icon: Clock,
    },
  };

  const currentCfg = STATUS_CONFIG[analise.status] || STATUS_CONFIG.SEM_PRAZO;
  const StatusIcon = currentCfg.Icon;

  if (compact) {
    return (
      <>
        <div
          onClick={(e) => {
            e.stopPropagation();
            setModalOpen(true);
          }}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold border cursor-pointer hover:opacity-85 transition-all ${currentCfg.bg} ${className}`}
          title={`Clique para ver esteira e análise completa | Prometido: ${analise.dataPrometidaFormatada} | Previsto: ${analise.dataPrevistaFormatada}`}
        >
          <StatusIcon size={12} />
          <span>{currentCfg.label}</span>
          {analise.dataPrevistaFormatada !== "-" && (
            <span className="opacity-90 font-mono font-normal">
              • Prev: {analise.dataPrevistaFormatada}
            </span>
          )}
          <ChevronRight size={10} className="opacity-60 shrink-0" />
        </div>

        {modalOpen && (
          <PrazoDetalhesModal
            order={orderWithItems}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`border rounded-xl p-3.5 space-y-3 transition-all shadow-xs ${currentCfg.bg} ${className}`}
      >
        {/* HEADER DO SUBCARD */}
        <div className="flex items-center justify-between gap-2 border-b border-black/5 pb-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Clock size={14} className="opacity-70" />
            <span className="text-[11px] font-black uppercase tracking-wider">
              Previsão de Prazo da Oficina
            </span>
            {modelo && (
              <span className="bg-white/80 border border-black/10 px-1.5 py-0.5 rounded text-[10px] font-bold font-mono">
                {modelo}
              </span>
            )}
            {rawItems.length > 1 && (
              <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded text-[9px] font-bold">
                Item {mostCritical.index + 1} de {rawItems.length} (Mais crítico)
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* STATUS BADGE */}
            <div
              className={`flex items-center gap-1 text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-tight shadow-xs ${currentCfg.badgeBg}`}
            >
              <StatusIcon size={11} />
              <span>{currentCfg.label}</span>
            </div>

            {/* BOTÃO PARA ABRIR MODAL COMPLETO DE ETAPAS */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
              className="p-1 hover:bg-black/5 rounded text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              title="Abrir detalhes completos e esteira de etapas"
            >
              <ExternalLink size={13} />
            </button>
          </div>
        </div>

        {/* COMPARATIVO DE DATAS (COM DIA DA SEMANA) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
          {/* Data Prometida */}
          <div className="bg-white/80 p-2.5 rounded-lg border border-black/5 flex flex-col justify-between">
            <span className="text-[9px] font-bold opacity-60 uppercase flex items-center gap-1">
              <Calendar size={10} /> Data Prometida (Venda)
            </span>
            <div className="font-extrabold text-blue-700 text-xs mt-1">
              {analise.dataPrometidaFormatada}
            </div>
            <span className="text-[9px] text-slate-400 font-mono mt-0.5">
              {analise.prazoPrometido ? `${analise.prazoPrometido} d.ú.` : "Não configurado"}
            </span>
          </div>

          {/* Data Prevista Dinâmica */}
          <div className="bg-white/80 p-2.5 rounded-lg border border-black/5 flex flex-col justify-between">
            <span className="text-[9px] font-bold opacity-60 uppercase flex items-center gap-1">
              <Clock size={10} /> Previsão Dinâmica de Entrega
            </span>
            <div
              className={`font-extrabold text-xs mt-1 ${
                analise.status === "PRONTO"
                  ? "text-emerald-700"
                  : analise.status === "ATRASADO" || analise.status === "VAI_ATRASAR"
                  ? "text-red-600"
                  : "text-emerald-700"
              }`}
            >
              {analise.status === "PRONTO" ? "Pronto na Oficina" : analise.dataPrevistaFormatada}
            </div>
            <span className="text-[9px] text-slate-400 font-mono mt-0.5">
              {analise.status === "PRONTO" ? "0 d.ú. restantes (Pronto)" : `~${formatDiasUteis(analise.faltam)} d.ú. restantes`}
            </span>
          </div>
        </div>

        {/* DIAGNÓSTICO DE RISCO E ATRASO ESPERADO */}
        <div className="bg-white/60 p-2 rounded-lg border border-black/5 flex flex-wrap items-center justify-between gap-1 text-[10px]">
          <div className="flex items-center gap-1.5 font-medium">
            <span className="font-bold text-slate-700">Diagnóstico:</span>
            <span className="text-slate-600">{analise.descricaoRisco}</span>
          </div>

          {analise.status === "PRONTO" ? (
            <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-mono font-extrabold text-[9px]">
              ✓ Oficina Concluída
            </span>
          ) : analise.atrasoEsperado > 0 ? (
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded font-mono font-bold text-[9px]">
              Atraso esperado: +{formatDiasUteis(analise.atrasoEsperado)} d.ú.
            </span>
          ) : analise.folga > 0 ? (
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-mono font-bold text-[9px]">
              Folga: {formatDiasUteis(analise.folga)} d.ú.
            </span>
          ) : null}
        </div>

        {/* LINK PARA ABRIR HISTÓRICO DE ETAPAS */}
        <div className="flex items-center justify-between pt-1 border-t border-black/5 text-[10px]">
          <span className="text-[9px] text-slate-400">
            {formatDiasUteis(analise.decorrido)} d.ú. decorridos da compra • Etapa: {analise.status === "PRONTO" ? "PEDIDO PRONTO" : (analise.etapaMapeada || "Fábrica")}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
            className="font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-0.5"
          >
            Ver Esteira de Etapas
            <ChevronRight size={12} />
          </button>
        </div>
      </div>

      {/* MODAL DETALHADO */}
      {modalOpen && (
        <PrazoDetalhesModal
          order={orderWithItems}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
